import Swiper, { SwiperElementProps, SwiperSlide } from '@components/Swiper'
import { useFullScreenGallery } from '@hooks/useFullScreenGallery'
import useFullScreenImagePortal from '@hooks/useFullScreenImagePortal'
import { isMobileUserAgent } from '@measures/responsive'
import { AdSlide, GalleryWidget, Slide } from '@widgets/Gallery/types'
import { isAdSlide } from '@widgets/Gallery/utils'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { css } from 'styled-components'
import SwiperClass from 'swiper/types/swiper-class'
import CloseButton from '../Common/CloseButton'
import SlideFooter from '../Common/SlideFooter'
import SlideImage from '../Common/SlideImage'
import useGalleryImagePreloading from '../utils/useGalleryImagePreloading'
import {
  useGallerySlideChangeTracking,
  useGallerySlideClickTracking,
} from '../utils/useGalleryTracking'
import GalleryAdSlide from './GalleryAdSlide'
import useShouldStretch from '@hooks/useShouldStretch'
import { StretchType } from '@contexts/storyTellingFullWidth'
import { printCSS } from '@utils/style'

type DeviceSwiperConfigType = Pick<
  SwiperElementProps,
  | 'navigation'
  | 'slidesPerView'
  | 'spaceBetween'
  | 'freeMode'
  | 'centeredSlides'
  | 'simulateTouch'
  | 'loop'
  | 'keyboard'
>

interface SwiperWrapperProps extends StretchType {
  isFullScreen: boolean
}

const SwiperWrapper = styled.div<SwiperWrapperProps>`
  ${({
    shouldStretch,
    shouldStretchToCardWidth,
    isFullScreen,
    shouldStretchToHeaderWidth,
    theme: {
      measures: { stretch, stretchToFitCard },
    },
  }) => css`
    > .swiper {
      width: 100%;
      padding-top: ${(2 / 3) * 100}%;
      position: relative;

      > .swiper-wrapper {
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    ${shouldStretch &&
    css`
      ${stretch('left', shouldStretchToHeaderWidth)}
      ${stretch('right', shouldStretchToHeaderWidth)}
    `}
    ${shouldStretchToCardWidth &&
    css`
      ${stretchToFitCard('left')}
      ${stretchToFitCard('right')}
    `}

    ${isFullScreen &&
    css`
      width: 100%;
      max-width: 994px;
    `}
  `}
`

const MultiImageGalleryPlaceholder = styled.div`
  width: 100%;
  position: relative;
  ${printCSS(css`
    display: none;
  `)}
`

const swiperConfig: DeviceSwiperConfigType = {
  loop: true,
  centeredSlides: true,
  spaceBetween: 16,
  slidesPerView: 1,
  freeMode: false,
  navigation: true,
  simulateTouch: true,
}

interface MultiImageGalleryProps {
  galleryTitle: GalleryWidget['title']
  galleryId: GalleryWidget['id']
  galleryUUID: GalleryWidget['uuid']
  slides: (Slide | AdSlide)[] | [Slide]
}

const MultiImageGallery: FunctionComponent<MultiImageGalleryProps> = ({
  slides,
  galleryTitle,
  galleryId,
  galleryUUID,
}) => {
  const swiperRef = useRef<SwiperClass | null>(null)
  const {
    isFullScreen,
    setIsFullScreen,
    hasControls,
    setHasControls,
    setIsGalleryWidget,
  } = useFullScreenGallery()
  const [appliedIndexNormalization, setAppliedIndexNormalization] =
    useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const { shouldPreloadImage, onPreloadNext } = useGalleryImagePreloading(
    slides.length
  )
  const { portalNode, enterFullScreen, exitFullScreen, InPortal, OutPortal } =
    useFullScreenImagePortal()
  const stretch = useShouldStretch()

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  const numberOfSlides = slides?.length
  const counterLabel = `${activeIndex + 1}/${numberOfSlides}`
  const activeCaption = isAdSlide(slides[activeIndex])
    ? ''
    : (slides[activeIndex] as Slide).caption
  const isMobile = isMobileUserAgent()

  /**
   * load the initial images only when in view,
   * and then every time the activeIndex changes
   */
  useEffect(() => {
    if (inView) {
      onPreloadNext(activeIndex)
    }
  }, [inView, onPreloadNext, activeIndex])

  useEffect(() => {
    setIsGalleryWidget(true)
  }, [setIsGalleryWidget])

  useEffect(() => {
    setTimeout(() => {
      swiperRef.current?.update()
    }, 0)
  }, [isFullScreen])

  const trackSlideClick = useGallerySlideClickTracking(
    activeIndex + 1,
    galleryId,
    galleryTitle
  )

  const enableKeyboardNavigation = useCallback(() => {
    swiperRef.current?.keyboard?.enable()
  }, [swiperRef])

  const disableKeyboardNavigation = useCallback(() => {
    swiperRef.current?.keyboard?.disable()
  }, [swiperRef])

  const handleEnterFullScreen = useCallback(() => {
    if (!isFullScreen) {
      setIsFullScreen(enterFullScreen())
      trackSlideClick()
      enableKeyboardNavigation()
    }
  }, [
    setIsFullScreen,
    enterFullScreen,
    isFullScreen,
    trackSlideClick,
    enableKeyboardNavigation,
  ])

  const handleLeaveFullScreen = useCallback(() => {
    setIsFullScreen(!exitFullScreen())
    disableKeyboardNavigation()
  }, [setIsFullScreen, exitFullScreen, disableKeyboardNavigation])

  const toggleControls = useCallback(() => {
    setHasControls(!hasControls)
  }, [setHasControls, hasControls])

  const handleSlideClick = useCallback(() => {
    if (isFullScreen && isMobile) {
      toggleControls()
    } else {
      handleEnterFullScreen()
    }
  }, [handleEnterFullScreen, toggleControls, isFullScreen, isMobile])

  const trackSlideChange = useGallerySlideChangeTracking(
    activeIndex + 1,
    galleryId,
    galleryTitle
  )

  /**
   * due to a known Swiper bug, onMount the realIndex (used for loops),
   * returns 1 instead of 0 and only gets updated after the first swipe.
   * This codes adjusts for that until fixed
   */
  const onSlideChange = useCallback(
    (swiper: SwiperClass) => {
      if (appliedIndexNormalization) {
        setActiveIndex(swiper.realIndex)
        trackSlideChange()
      } else {
        setAppliedIndexNormalization(true)
      }
    },
    [appliedIndexNormalization, trackSlideChange]
  )

  const onInit = useCallback((swiper: any) => {
    swiperRef.current = swiper
  }, [])

  return !portalNode ? null : (
    <MultiImageGalleryPlaceholder ref={ref}>
      <InPortal node={portalNode}>
        <SwiperWrapper isFullScreen={isFullScreen} {...stretch}>
          <CloseButton onClose={handleLeaveFullScreen} />
          <Swiper
            {...swiperConfig}
            onInit={onInit}
            initialSlide={0}
            onSlideChange={onSlideChange}
            credit={
              (slides?.find((_, index) => index === activeIndex) as Slide)
                ?.credit
            }
            slides={slides?.map((item: Slide | AdSlide, index: number) => (
              <SwiperSlide key={index}>
                {isAdSlide(item) ? (
                  <GalleryAdSlide
                    isActive={activeIndex === index}
                    galleryUUID={galleryUUID}
                    adSlide={item}
                    index={index}
                  />
                ) : (
                  <SlideImage
                    image={item.image}
                    onClick={handleSlideClick}
                    shouldPreload={shouldPreloadImage(index)}
                  />
                )}
              </SwiperSlide>
            ))}
          />
          <SlideFooter counterLabel={counterLabel} caption={activeCaption} />
        </SwiperWrapper>
      </InPortal>
      {!isFullScreen && <OutPortal node={portalNode} />}
    </MultiImageGalleryPlaceholder>
  )
}

export default MultiImageGallery
